import React, { useRef, useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiCheckCircleOutline } from "@mdi/js";
import { Button, Modal } from "@heroui/react";

export interface PayoutRecipient {
  name: string;
  address?: string;
  amount: number;
}

interface PayoutModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  recipients: PayoutRecipient[];
  totalAmount: number;
  onSuccess?: () => void;
}

type Step = "confirm" | "sms" | "success";

const formatMoney = (v: number) =>
  v.toLocaleString("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  });

function SmsStep({
  onBack,
  onSubmit,
}: {
  onBack: () => void;
  onSubmit: () => void;
}) {
  const [code, setCode] = useState(["", "", "", ""]);
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    refs[0].current?.focus();
  }, []);

  const handleChange = (i: number, val: string) => {
    const ch = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[i] = ch;
    setCode(next);
    if (ch && i < 3) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (
    i: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      refs[i - 1].current?.focus();
    }
  };

  const filled = code.every((c) => c !== "");

  return (
    <>
      <Modal.Header className="pm-modal__header">
        <Modal.Heading className="pm-modal__heading">
          Подтверждение
        </Modal.Heading>
        <p className="pm-modal__subtext">
          Введите 4-значный код из SMS, отправленного на ваш номер
        </p>
      </Modal.Header>
      <Modal.Body className="pm-modal__body pm-modal__body--center">
        <div className="pm-sms-inputs">
          {code.map((val, i) => (
            <input
              key={i}
              ref={refs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={val}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className={`pm-sms-input ${val ? "pm-sms-input--filled" : ""}`}
              aria-label={`Цифра ${i + 1}`}
            />
          ))}
        </div>
        <p className="pm-sms-hint">
          Не получили код?{" "}
          <button type="button" className="pm-sms-resend">
            Отправить повторно
          </button>
        </p>
      </Modal.Body>
      <Modal.Footer className="pm-modal__footer">
        <Button variant="tertiary" onPress={onBack} data-testid="payout-back-button">
          Назад
        </Button>
        <Button
          variant="primary"
          isDisabled={!filled}
          onPress={onSubmit}
          data-testid="payout-confirm-button"
        >
          Подтвердить
        </Button>
      </Modal.Footer>
    </>
  );
}

const PayoutModal: React.FC<PayoutModalProps> = ({
  isOpen,
  onOpenChange,
  recipients,
  totalAmount,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>("confirm");

  useEffect(() => {
    if (isOpen) setStep("confirm");
  }, [isOpen]);

  const handleClose = () => {
    onOpenChange(false);
    if (step === "success") onSuccess?.();
  };

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={handleClose}>
      <Modal.Container size="sm">
        <Modal.Dialog className="pm-modal" data-testid="payout-modal">
          {step !== "success" && <Modal.CloseTrigger />}

          {step === "confirm" && (
            <>
              <Modal.Header className="pm-modal__header">
                <Modal.Heading className="pm-modal__heading">
                  Выплата контрагентам
                </Modal.Heading>
                <div className="pm-total">
                  <span className="pm-total__label">Итого к выплате</span>
                  <span className="pm-total__amount">
                    {formatMoney(totalAmount)}
                  </span>
                </div>
              </Modal.Header>
              <Modal.Body className="pm-modal__body">
                <div className="pm-recipients">
                  {recipients.map((r, i) => (
                    <div key={i} className="pm-recipient">
                      <div className="pm-recipient__info">
                        <span className="pm-recipient__name">{r.name}</span>
                        {r.address && (
                          <span className="pm-recipient__address">
                            {r.address}
                          </span>
                        )}
                      </div>
                      <span className="pm-recipient__amount">
                        {formatMoney(r.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </Modal.Body>
              <Modal.Footer className="pm-modal__footer">
                <Button variant="tertiary" onPress={handleClose} data-testid="payout-cancel-button">
                  Отмена
                </Button>
                <Button
                  variant="primary"
                  onPress={() => setStep("sms")}
                  data-testid="payout-continue-button"
                >
                  Продолжить
                </Button>
              </Modal.Footer>
            </>
          )}

          {step === "sms" && (
            <SmsStep
              onBack={() => setStep("confirm")}
              onSubmit={() => setStep("success")}
            />
          )}

          {step === "success" && (
            <>
              <Modal.Body className="pm-modal__body pm-modal__body--success">
                <div className="pm-success">
                  <div className="pm-success__icon">
                    <Icon path={mdiCheckCircleOutline} size={2.5} />
                  </div>
                  <span className="pm-success__title">Выплата выполнена</span>
                  <span className="pm-success__amount">
                    {formatMoney(totalAmount)}
                  </span>
                  <span className="pm-success__hint">
                    {recipients.length === 1
                      ? `Переведено: ${recipients[0].name}`
                      : `Переведено ${recipients.length} контрагентам`}
                  </span>
                </div>
              </Modal.Body>
              <Modal.Footer className="pm-modal__footer pm-modal__footer--center">
                <Button variant="primary" onPress={handleClose} data-testid="payout-done-button">
                  Готово
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};

PayoutModal.displayName = "PayoutModal";
export default PayoutModal;
